import { useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Music, LayoutDashboard, Users, Target, SlidersHorizontal, FileBarChart, LogOut, ShieldCheck, KeyRound, Award, FolderOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/staff/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/staff/choristers", label: "Choristers", icon: Users },
  { to: "/staff/quests", label: "Quests", icon: Target },
  { to: "/staff/content", label: "Our Content", icon: FolderOpen },
  { to: "/staff/curriculum", label: "Curriculum", icon: KeyRound },
  { to: "/staff/modules", label: "Learning Modules", icon: SlidersHorizontal, admin: true },
  { to: "/staff/reports", label: "Reports", icon: FileBarChart },
  { to: "/staff/certificates", label: "Certificates", icon: Award },
];

export default function StaffLayout() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const profile = session?.profile;
  const isAdmin = profile?.role === "admin" || profile?.role === "super_admin";
  const navItems = useMemo(() => NAV.filter((n) => !n.admin || isAdmin), [isAdmin]);

  return (
    <div className="min-h-screen bg-[#F3EFE0] flex flex-col lg:flex-row" data-testid="staff-layout">
      <aside className="bg-[#0B192C] text-[#FAF7EE] lg:w-60 lg:min-h-screen shrink-0">
        <div className="px-5 py-4 flex flex-col lg:items-stretch gap-2 lg:gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:px-2 lg:py-3">
              <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
              <div>
                <span className="font-display font-bold block leading-tight">ChoirQuest</span>
                <span className="text-[10px] text-[#FAF7EE]/50 uppercase tracking-widest">Staff Portal</span>
              </div>
            </div>
            <button onClick={() => { logout(); navigate("/staff"); }} data-testid="staff-logout-btn"
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-[#FAF7EE]/60 hover:bg-white/10 min-h-[48px]">
              <LogOut className="w-4 h-4" aria-hidden /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto max-w-full pb-1" data-testid="staff-nav">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors min-h-[48px] ${
                    isActive ? "bg-[#C69B3D] text-[#0B192C]" : "text-[#FAF7EE]/70 hover:bg-white/10"}`}>
                <Icon className="w-4 h-4 shrink-0" aria-hidden /> {label}
              </NavLink>
            ))}
            {profile?.role === "super_admin" && (
              <NavLink to="/super" data-testid="nav-super-admin"
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold text-[#C69B3D] hover:bg-white/10 whitespace-nowrap min-h-[48px]">
                <ShieldCheck className="w-4 h-4" aria-hidden /> Super Admin
              </NavLink>
            )}
          </nav>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10">
          <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#566270]">{profile?.institution_name || "ChoirQuest"}</p>
            <p className="text-sm text-[#1C242B] font-semibold" data-testid="staff-name">{profile?.name} · {profile?.role === "admin" ? "Institution Admin" : profile?.role}</p>
          </div>
        </div>
        {profile?.email_verified === false && (
          <div className="mb-6 rounded-xl bg-[#F6EED8] border border-[#C69B3D]/50 px-5 py-4 text-sm text-[#1C242B]" data-testid="unverified-banner">
            <strong>Please confirm your email address.</strong> We sent a confirmation link to {profile.email} when you signed up.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
